import Router from 'next/router';
import NProgress from 'nprogress';

import getLayerStore from '@stores/LayerStore';
import { mkAssetUrl } from '@lib/utils';
import { MAP_LAYERS } from '@lib/const';

import type { ApolloClient } from '@lib/apolloClient';
import type { LatLngCoordinates } from '@lib/types';
import type { Geometry } from 'geojson';
import type { GeoJSONSource, Map, Marker } from 'mapbox-gl';

const LayerStore = getLayerStore();

export function init(map: Map): void {
  map.addSource(MAP_LAYERS.polygons, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Point',
        coordinates: [],
      },
    },
  });

  map.addLayer({
    id: MAP_LAYERS.polygons,
    type: 'line',
    source: MAP_LAYERS.polygons,
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': 'black',
      'line-width': 3,
    },
  });

  map.setLayoutProperty(MAP_LAYERS.polygons, 'visibility', 'none');
}

export function updatePolygon(map: Map, geometry: Geometry): void {
  (map.getSource(MAP_LAYERS.polygons) as GeoJSONSource)?.setData({
    type: 'Feature',
    properties: {},
    geometry,
  });
}

export function hidePolygon(map: Map): void {
  map.setLayoutProperty(MAP_LAYERS.polygons, 'visibility', 'none');
}

export function setMarker(map: Map, marker: Marker, lngLat: LatLngCoordinates): void {
  marker.setLngLat(lngLat).addTo(map);
  marker.getElement().style.display = 'block';
}

export function hideMarker(marker: Marker): void {
  marker.remove();
  marker.getElement().style.display = 'none';
}

export async function handleMapClick(
  apolloClient: ApolloClient,
  map: Map,
  marker: Marker,
  lngLat: LatLngCoordinates,
  pushRoute: boolean,
): Promise<void> {
  const [lng, lat] = lngLat;
  NProgress.start();
  const coordinates = await LayerStore.getArea(apolloClient, lat, lng);
  NProgress.done();

  if (coordinates?.areasByCoordinates?.areas?.length) {
    const area = coordinates.areasByCoordinates.areas[0];
    const { distance, url } = area;
    const polygon = area.polygon as Geometry;

    if (pushRoute) {
      const urlFromResponse = mkAssetUrl(url);

      Router.push(
        `${urlFromResponse}?center=${map.getCenter().toArray().join(',')}&zoom=${parseInt(
          map.getZoom().toFixed(),
          10,
        )}&marker=${lng},${lat}`,
      );
    }

    if (distance === 0) {
      updatePolygon(map, polygon);

      map.setLayoutProperty(MAP_LAYERS.polygons, 'visibility', 'visible');
      setMarker(map, marker, lngLat);
    } else {
      setMarker(map, marker, lngLat);
      hidePolygon(map);
    }
  }
}
